import type * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import ButtonComponent from '../../../components/UI/Button/Button.component';
import drawImage, {
  type DrawImage,
} from '../../../components/UserProfileAvatar/AvatarEditor/helpers/drawImage';
import { GLOBAL_CONSTANTS } from '../../../constants/globals';
import '../user-profile-avatar.scss';

interface AvatarEditorProps {
  initialImage: string;
  avatarKey: string;
  onSave: (imageData: string) => void;
  handleCancelEditAvatar: () => void;
}

const AvatarEditor = (props: AvatarEditorProps) => {
  const { handleCancelEditAvatar, initialImage, onSave } = props;

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [aspectRatioLocked, setAspectRatioLocked] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [initialAvatarImage, setInitialAvatarImage] = useState<string | null>(
    null,
  );

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (initialImage !== null) {
      setInitialAvatarImage(initialImage);
    }
  }, [initialImage]);

  const drawImageArgs = useMemo(
    () => ({
      canvasRef,
      imageRef,
      position,
      rotation,
      size,
      zoom,
    }),
    [position, rotation, size, zoom],
  );

  useEffect(() => {
    if (initialAvatarImage !== null) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = initialAvatarImage;
      image.onload = () => {
        imageRef.current = image;
        const aspectRatio = image.width / image.height;
        setSize((prev) => {
          const newHeight = Math.round(prev.width / aspectRatio);
          if (prev.height !== newHeight) {
            return { ...prev, height: newHeight };
          }
          return prev;
        });
        setImageLoaded(true);
      };
      image.onerror = () => {
        const fallbackImage = new Image();
        fallbackImage.src = initialAvatarImage;
        fallbackImage.onload = () => {
          imageRef.current = fallbackImage;
          const aspectRatio = fallbackImage.width / fallbackImage.height;
          setSize((prev) => {
            const newHeight = Math.round(prev.width / aspectRatio);
            if (prev.height !== newHeight) {
              return { ...prev, height: newHeight };
            }
            return prev;
          });
          setImageLoaded(true);
        };
      };
    }
  }, [initialAvatarImage]);

  useEffect(() => {
    if (imageRef.current) {
      drawImage(drawImageArgs as DrawImage);
    }
  }, [drawImageArgs]);

  useEffect(() => {
    if (imageLoaded) {
      drawImage(drawImageArgs as DrawImage);
    }
  }, [drawImageArgs, imageLoaded]);

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZoom(parseFloat(e.target.value));
  };

  const handleRotationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRotation(parseInt(e.target.value));
  };

  const handleSizeChange = (dimension: 'width' | 'height', value: string) => {
    const newValue = parseInt(value);
    if (aspectRatioLocked) {
      const aspectRatio = size.width / size.height;
      if (dimension === 'width') {
        setSize({
          width: newValue,
          height: Math.round(newValue / aspectRatio),
        });
      } else {
        setSize({
          width: Math.round(newValue * aspectRatio),
          height: newValue,
        });
      }
    } else {
      setSize((prev) => ({ ...prev, [dimension]: newValue }));
    }
  };

  const handleAspectRatioLockChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAspectRatioLocked(event.target.checked);
    if (event.target.checked) {
      const aspectRatio = size.width / size.height;
      setSize((prev) => ({
        ...prev,
        height: Math.round(prev.width / aspectRatio),
      }));
    }
  };
  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const side = Math.min(canvas.width, canvas.height);
      const offscreen = document.createElement('canvas');
      offscreen.width = side;
      offscreen.height = side;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return;
      const offsetX = (canvas.width - side) / 2;
      const offsetY = (canvas.height - side) / 2;
      ctx.drawImage(canvas, offsetX, offsetY, side, side, 0, 0, side, side);
      const imageData = offscreen.toDataURL('image/png');
      onSave(imageData);
    } catch (error) {
      console.error('Error exporting canvas data:', error);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      // Calculate the change in mouse position
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;

      // Update the position state in the opposite direction of the mouse movement
      setPosition((prevPosition) => ({
        x: prevPosition.x - dx,
        y: prevPosition.y - dy,
      }));

      // Update the drag start position for the next move
      setDragStart({
        x: e.clientX,
        y: e.clientY,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div
      id={'AVATAR__EDITOR__VIEW'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{ marginBottom: '1rem', position: 'relative', cursor: 'move' }}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        className={'AVATAR__EDITOR__CANVAS__CONTAINER'}
      >
        <canvas
          ref={canvasRef}
          height={size.height}
          style={{ borderRadius: '50%' }}
          width={size.width}
        />
      </div>

      <div
        style={{
          width: '100%',
          maxWidth: '20rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <div>
          <label
            htmlFor="avatar-zoom"
            style={{ display: 'block', marginBottom: '0.5rem' }}
          >
            Zoom
          </label>
          <input
            id="avatar-zoom"
            type="range"
            max={3}
            min={0.1}
            step={0.1}
            value={zoom}
            onChange={handleZoomChange}
            style={{ width: '100%' }}
          />
        </div>

        <div>
          <label
            htmlFor="avatar-rotation"
            style={{ display: 'block', marginBottom: '0.5rem' }}
          >
            Rotation
          </label>
          <input
            id="avatar-rotation"
            type="range"
            max={360}
            min={0}
            step={1}
            value={rotation}
            onChange={handleRotationChange}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <div>
            <label
              htmlFor="avatar-width"
              style={{ display: 'block', marginBottom: '0.5rem' }}
            >
              Width
            </label>
            <input
              id="avatar-width"
              type="number"
              max={500}
              min={50}
              value={size.width}
              onChange={(e) => handleSizeChange('width', e.target.value)}
              style={{
                width: '5rem',
                padding: '0.25rem',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius)',
              }}
            />
          </div>
          <div>
            <label
              htmlFor="avatar-height"
              style={{ display: 'block', marginBottom: '0.5rem' }}
            >
              Height
            </label>
            <input
              id="avatar-height"
              type="number"
              max={500}
              min={50}
              value={size.height}
              onChange={(e) => handleSizeChange('height', e.target.value)}
              style={{
                width: '5rem',
                padding: '0.25rem',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius)',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={aspectRatioLocked}
            id="aspectRatioLock"
            onChange={handleAspectRatioLockChange}
          />
          <label htmlFor="aspectRatioLock">Lock Aspect Ratio</label>
        </div>
        <div className={'AVATAR__Buttons__Container__Wide__Mode'}>
          <ButtonComponent variant="primary" onClick={handleSave}>
            {'Save Avatar'}
          </ButtonComponent>
          <ButtonComponent
            className="Preferences__Cancel__AVATAR__Button"
            variant="outline"
            onClick={handleCancelEditAvatar}
          >
            {GLOBAL_CONSTANTS.CANCEL}
          </ButtonComponent>
        </div>
      </div>
    </div>
  );
};

export default AvatarEditor;
